import type { Scene } from '@babylonjs/core/scene';

import type { OffHandClass } from '../../net/protocol';
import { type Cell, GEAR, WEAPON } from './atlas';
import type { CellLighting, MaterialName, ModelPalette } from './palette';
import { box, cylinder, joint, lathe, sphere, torus, wedge, type Rig } from './rig';
import { buildWeaponParts, weaponProfile } from './weapons';

/**
 * What the off hand holds, one model per `offhand_type` — Phase 41.
 *
 * Until this change every one-handed character carried the same kite shield, because the
 * wire said nothing about the off hand and only its *absence* could be derived (a two-hander
 * blocks it). `EntitySnapshot.OffHand` now carries a closed vocabulary — `BUCKLER`, `SHIELD`,
 * `TOME`, `ORB` from the item's `offhand_type`, or `DAGGER` when the off hand holds a second
 * blade — and this file draws one silhouette per member, exactly as `weapons.ts` does for the
 * main hand.
 *
 * **Cosmetic only**, on the same terms as the weapons: a buckler's block chance comes from
 * its `base_stats`, and nothing here reads a number the simulation reads. The kite shield
 * stays the default for an absent field, so a character with nothing in the off hand looks
 * exactly as they always did — the rule the longsword and the bare body follow.
 *
 * Everything built here goes into `rig.heldParts`: a held thing is not the body and the
 * collider must not grow with a tower shield any more than with a staff.
 */
export interface OffHandProfile {
  /** Which shape to build. */
  shape: 'round' | 'kite' | 'book' | 'orb' | 'blade';
  /** Across, along the forearm, and thick. For an orb, its diameter three times. */
  size: readonly [number, number, number];
  /** The face's painting and its flat fallback. */
  cell: Cell;
  material: MaterialName;
  lighting?: CellLighting;
}

const PROFILES: Record<OffHandClass, OffHandProfile> = {
  // A round wooden buckler. Small — a parrying shield — and a disc, which a kite is not.
  BUCKLER: {
    shape: 'round',
    size: [0.4, 0.4, 0.05],
    cell: WEAPON.woodShield,
    material: 'rust',
  },
  // The kite shield the crusader always carried, and still the default.
  SHIELD: {
    shape: 'kite',
    size: [0.42, 0.66, 0.06],
    cell: GEAR.ironPlate,
    material: 'darkSteel',
  },
  // A grimoire, closed, clasped against the forearm. Its cover is the gold-framed panel cell,
  // which was painted as trim and is exactly what a book's board looks like.
  TOME: {
    shape: 'book',
    size: [0.26, 0.32, 0.09],
    cell: GEAR.trimGold,
    material: 'cloth',
  },
  // A focus that floats over the palm and is lit from within — the only off-hand that glows,
  // so a caster's second hand reads as a caster's from across the room.
  ORB: {
    shape: 'orb',
    size: [0.2, 0.2, 0.2],
    cell: WEAPON.voidOrb,
    material: 'ember',
    lighting: 'selfLit',
  },
  // A second dagger: the main-hand dagger's own profile, gripped the same way.
  DAGGER: {
    shape: 'blade',
    size: [0.14, 0.36, 0.07],
    cell: WEAPON.steelEdge,
    material: 'steel',
  },
};

/**
 * Every off-hand class a profile exists for. Held to `config.OffHandTypes` (plus `DAGGER`,
 * which is a weapon family rather than an `offhand_type`) by
 * `grind/internal/item/offhand_mesh_test.go`, in both directions.
 */
export const OFFHAND_CLASSES: readonly OffHandClass[] = ['BUCKLER', 'SHIELD', 'TOME', 'ORB', 'DAGGER'];

/** The profile for a wire value; the kite shield for an absent or unknown one. */
export function offHandProfile(kind?: string | null): OffHandProfile {
  if (!kind) return PROFILES.SHIELD;
  return PROFILES[kind as OffHandClass] ?? PROFILES.SHIELD;
}

/**
 * Builds the off hand's item into `rig.heldParts`, or nothing when the main hand blocks it.
 *
 * A shield and a tome are **strapped to the forearm** (`rig.foreL`), so they ride the elbow
 * and stay flat along the arm; an orb and a dagger are **in the hand** (`rig.offhand`), so
 * they swing with the wrist. Same split a real arm makes.
 */
export function buildOffHand(
  scene: Scene,
  rig: Rig,
  palette: ModelPalette,
  kind: string | undefined,
  blocked: boolean,
): void {
  if (blocked) return;
  const parts = rig.heldParts;
  const profile = offHandProfile(kind);
  const face = palette.cell(profile.cell, profile.material, profile.lighting);
  const dark = palette.get('darkSteel');
  const bronze = palette.get('bronze');
  const [w, h, d] = profile.size;

  // Outward from the forearm and a little forward, so the shield face is toward the enemy.
  const X = 0.13;

  switch (profile.shape) {
    case 'round':
      // A disc with its axis along the arm's outward X, so the painted face is the cap
      // pointing away from the body — a FRONT cell on a cylinder cap is a picture in a circle.
      cylinder(scene, rig.foreL, parts, {
        name: 'off-buckler',
        height: d,
        diameterTop: w,
        diameterBottom: w,
        at: [X, -0.1, 0.04],
        rotation: [0, 0, Math.PI / 2],
        material: face,
        tessellation: 12,
      });
      sphere(scene, rig.foreL, parts, {
        name: 'off-boss',
        size: [0.07, 0.12, 0.12],
        at: [X + d / 2, -0.1, 0.04],
        material: bronze,
        segments: 4,
      });
      break;

    case 'kite': {
      // A rounded top over a tapered point: the top half is a flattened dome, the bottom a
      // wedge to the tip, and a boss where the two meet. Reads as a kite from every side.
      const topH = h * 0.45;
      sphere(scene, rig.foreL, parts, {
        name: 'off-shield',
        size: [d, topH * 2, w],
        at: [X, -0.12, 0.04],
        material: face,
        segments: 6,
        slice: 0.5,
      });
      wedge(scene, rig.foreL, parts, {
        name: 'off-shield-point',
        size: [w, h * 0.55, d],
        taper: 0.05,
        flip: true,
        at: [X, -0.12 - (h * 0.55) / 2, 0.04],
        // The wedge's width runs along X; a quarter turn puts it across the arm (Z) so the
        // shield is a flat plate edge-on to the shoulder, not a cone hanging under it.
        rotation: [0, Math.PI / 2, 0],
        material: face,
      });
      sphere(scene, rig.foreL, parts, {
        name: 'off-boss',
        size: [0.06, 0.14, 0.14],
        at: [X + d / 2, -0.12, 0.04],
        material: bronze,
        segments: 4,
      });
      break;
    }

    case 'book':
      // Two boards and the pages between: the painted board outward, pale pages showing.
      box(scene, rig.foreL, parts, {
        name: 'off-tome',
        size: [d, h, w],
        at: [X, -0.1, 0.03],
        material: face,
      });
      box(scene, rig.foreL, parts, {
        name: 'off-tome-pages',
        size: [d * 0.7, h * 0.92, w * 0.9],
        at: [X + 0.01, -0.1, 0.03 + w * 0.06],
        material: palette.get('bone'),
      });
      torus(scene, rig.foreL, parts, {
        name: 'off-tome-clasp',
        diameter: h * 0.5,
        thickness: 0.025,
        at: [X, -0.1, 0.03],
        rotation: [0, 0, Math.PI / 2],
        material: bronze,
        tessellation: 8,
      });
      break;

    case 'orb':
      // Cupped in the palm on a small claw and floating just clear of it.
      lathe(scene, rig.offhand, parts, {
        name: 'off-orb-claw',
        profile: [
          [0.03, 0],
          [0.07, 0.05],
          [0.05, 0.08],
        ],
        at: [0, -0.06, 0.1],
        material: dark,
        tessellation: 6,
      });
      sphere(scene, rig.offhand, parts, {
        name: 'off-orb',
        size: [w, h, d],
        at: [0, 0.06, 0.12],
        material: face,
        segments: 8,
      });
      break;

    case 'blade': {
      // The dagger profile, under a grip node of its own so it is held like the other one.
      const grip = joint('off-grip', rig.offhand, [0, 0, 0], scene);
      const dagger = weaponProfile('DAGGER');
      grip.rotation.x = dagger.gripPitch;
      buildWeaponParts(scene, grip, parts, palette, dagger, 'off');
      break;
    }
  }
}
